import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { flow, isNumber } from 'lodash';
import { useLocale } from 'locale';
import {
  displayLevelSelector,
  pricingTierSelector,
  unitPageBulletinSelector,
  isAgentConsoleUserSelector,
  safeDealsAgentsByIdsSelector,
  safeDealsOfficesByIdsSelector,
} from 'store/state/domainData/selectors';
import { useScreenBreakpoint } from 'consts/breakpoints';
import { State } from 'store/state';
import { SmallText } from 'ds/components/typography';
import {
  HistoryHebrewItem,
  historyPricesRawDataSelector,
  PricesTabType,
  RawHistoryItem,
} from 'store/state/selectors/insights/prices';
import { InfoRow, SeparatorWrapper } from './styled';
import { ExpandWrapper } from '../../styled';
import { UnitExpendedLabel } from './columns/styled';
import { pricesTabSelector } from 'store/state/insightsContext/selectors';
import AffiliationSection from 'components/widget/prices/prices-table/tables/history/affiliation/AffiliationSection';
import { IOfficeAgentDisplayLevel } from 'utils/entities';
import { IGetPublicRealEstateAgentsByIdsResponseItem, IGetPublicRealEstateOfficesByIdsResponseItem, PricingTier } from 'store/sagas/apiService/types';
import { filterItemData } from '../../utils';

interface ExpandedInfoProps {
  historyPricesRawData: RawHistoryItem[];
  item: HistoryHebrewItem;
  currentBulletinId: string;
  isCompareTab: boolean;
  displayLevel: IOfficeAgentDisplayLevel;
  pricingTier: PricingTier;
  isAgentConsoleUser: boolean;
  dealsAgentsData?: IGetPublicRealEstateAgentsByIdsResponseItem[];
  dealsOfficesData?: IGetPublicRealEstateOfficesByIdsResponseItem[];
}

const ExpandedInfo: React.FC<ExpandedInfoProps> = ({
  historyPricesRawData,
  currentBulletinId,
  isCompareTab,
  displayLevel,
  item,
  pricingTier,
  isAgentConsoleUser,
  dealsAgentsData,
  dealsOfficesData,
}) => {
  const { t } = useLocale();
  const screen = useScreenBreakpoint();
  const hasFloor = isNumber(item.floor);

  const rawItem = historyPricesRawData.find(hbp => hbp.saleId === item.id);

  const hideAffiliationButton = !isAgentConsoleUser;

  const buyer = useMemo(() => filterItemData(item.buyer, dealsAgentsData, dealsOfficesData), [ item.buyer, dealsOfficesData, dealsAgentsData ]);
  const seller = useMemo(() => filterItemData(item.seller, dealsAgentsData, dealsOfficesData), [ item.seller, dealsOfficesData, dealsAgentsData ]);

  return (
    <ExpandWrapper>
      {screen === 3 && (item.year || hasFloor) ? (
        <InfoRow>
          <UnitExpendedLabel>
            <SmallText>
              {hasFloor ? t('widget.prices.floor', { floor: item.floor }) : null}
              {item.year && hasFloor ? <SeparatorWrapper>•</SeparatorWrapper> : null}
              {item.year ? `${t('widget.prices.columns.year')}-${item.year}` : null}
            </SmallText>
          </UnitExpendedLabel>
        </InfoRow>
      ) : null}
      {currentBulletinId !== item.id && isAgentConsoleUser && !buyer && !seller && !isCompareTab && displayLevel !== IOfficeAgentDisplayLevel.Minimal && pricingTier !== PricingTier.Discounted ? (
        <InfoRow agentConsole>
          <AffiliationSection
            address={rawItem && rawItem.addressRecord}
            item={item}
            hideAffiliationButton={hideAffiliationButton}
            buyer={buyer}
            seller={seller}
          />
       </InfoRow>
      ) : null}
      {buyer || seller ? (
        <InfoRow agentConsole>
          <AffiliationSection
            address={rawItem && rawItem.addressRecord}
            item={item}
            hideAffiliationButton={hideAffiliationButton}
            buyer={buyer}
            seller={seller}
          />
        </InfoRow>
      ) : null}
    </ExpandWrapper>
  );
};

const bulletinIdSelector = flow(unitPageBulletinSelector, u => u && u.id);
const isCompareTabSelector = flow(pricesTabSelector, tab => tab === PricesTabType.Compare);


const mapStateToProps = (state: State) => ({
  historyPricesRawData: historyPricesRawDataSelector(state),
  displayLevel: displayLevelSelector(state),
  pricingTier: pricingTierSelector(state),
  currentBulletinId: bulletinIdSelector(state),
  isCompareTab: isCompareTabSelector(state),
  isAgentConsoleUser: isAgentConsoleUserSelector(state),
  dealsAgentsData: safeDealsAgentsByIdsSelector(state),
  dealsOfficesData: safeDealsOfficesByIdsSelector(state),
});

export const ExpandedInfoILInner = connect(mapStateToProps)(ExpandedInfo);
